<template>
  <div
    v-if="isVisible"
    class="free-shipping"
    :class="{
      'free-shipping--pill': isPillVariant,
      'free-shipping--with-pill': isPillVariant && amountUntilFreeShipping,
    }"
  >
    <p class="free-shipping__copy">
      <template v-if="isPillVariant">{{ pillCopy }}</template>
      <template v-else>
        <span v-for="(line, index) in defaultLines" :key="index">{{
          line
        }}</span>
      </template>
    </p>

    <div
      v-if="showTrack"
      class="free-shipping__track"
      :style="{ '--progress': progress }"
    >
      <div class="free-shipping__fill" />
      <span v-if="!isPillVariant" class="free-shipping__knob" />
      <span
        v-else-if="!hasQualifiedForFreeShipping"
        class="free-shipping__goal"
      />
      <span
        v-if="isPillVariant && amountUntilFreeShipping"
        class="free-shipping__pill"
      >
        Add {{ amountUntilFreeShipping }}
      </span>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import { shippingRules } from "@/config.js";
import { getFeatureToggles } from "@/helpers/featureToggles.js";

export default {
  name: "FreeShippingProgress",
  props: {
    subtotal: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapGetters("app", ["currencyCode"]),
    ...mapState("app", ["userCountry"]),
    isPillVariant() {
      return Boolean(getFeatureToggles().bagFreeShippingVariant);
    },
    basketCurrencyCode() {
      return this.subtotal?.currencyCode || this.currencyCode || null;
    },
    shippingConfig() {
      const code = this.basketCurrencyCode?.toLowerCase();

      if (!code) {
        return null;
      }

      return shippingRules[code] || null;
    },
    isEligibleForFreeShipping() {
      const freeForCountry = this.shippingConfig?.freeForCountry;

      if (freeForCountry) {
        return this.userCountry === freeForCountry;
      }

      return true;
    },
    freeShippingThreshold() {
      const amount = this.shippingConfig?.freeAboveAmount?.amount;

      if (amount == null) {
        return null;
      }

      return Number(amount);
    },
    cartSubtotalAmount() {
      return Number(this.subtotal?.amount || 0);
    },
    amountUntilFreeShipping() {
      if (
        !this.isEligibleForFreeShipping ||
        this.freeShippingThreshold == null ||
        !this.basketCurrencyCode
      ) {
        return null;
      }

      const difference = this.freeShippingThreshold - this.cartSubtotalAmount;

      if (difference <= 0) {
        return null;
      }

      return this.$filters.toCurrency(difference, this.basketCurrencyCode, 0);
    },
    formattedThreshold() {
      if (this.freeShippingThreshold == null || !this.basketCurrencyCode) {
        return null;
      }

      return this.$filters.toCurrency(
        this.freeShippingThreshold,
        this.basketCurrencyCode,
        0
      );
    },
    hasQualifiedForFreeShipping() {
      if (
        !this.isEligibleForFreeShipping ||
        this.freeShippingThreshold == null
      ) {
        return false;
      }

      return this.cartSubtotalAmount >= this.freeShippingThreshold;
    },
    progress() {
      if (this.hasQualifiedForFreeShipping) {
        return 1;
      }

      if (!this.freeShippingThreshold) {
        return 0;
      }

      return Math.min(
        1,
        Math.max(0, this.cartSubtotalAmount / this.freeShippingThreshold)
      );
    },
    showTrack() {
      return this.freeShippingThreshold != null;
    },
    defaultLines() {
      if (this.amountUntilFreeShipping) {
        return [
          "You've almost qualified for Free Shipping.",
          `Spend over ${this.formattedThreshold}. ${this.amountUntilFreeShipping} to go`,
        ];
      }

      if (this.hasQualifiedForFreeShipping) {
        return ["You've qualified for Free Shipping."];
      }

      return [];
    },
    pillCopy() {
      if (this.amountUntilFreeShipping) {
        return `You're ${this.amountUntilFreeShipping} away from qualifying for Free Shipping.`;
      }

      if (this.hasQualifiedForFreeShipping) {
        return "You've qualified for Free Shipping.";
      }

      return "";
    },
    isVisible() {
      if (this.isPillVariant) {
        return Boolean(this.pillCopy);
      }

      return this.defaultLines.length > 0;
    },
  },
};
</script>

<style lang="scss" scoped>
.free-shipping {
  margin: 0 0 4px;
  text-align: center;

  &__copy {
    @include utility-label(15px);
    line-height: 1.35;
    margin: 0 0 16px;

    span {
      display: block;
    }
  }

  &__track {
    position: relative;
    height: 2px;
    margin: 0 0 16px;
    background: $color-grey;
    border-radius: 8px;
  }

  &__fill {
    position: absolute;
    top: 0;
    left: 0;
    height: 2px;
    width: calc(var(--progress) * 100%);
    max-width: 100%;
    background: $color-black;
    border-radius: 8px;
  }

  &__knob {
    position: absolute;
    top: 50%;
    left: calc(var(--progress) * 100%);
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: $color-black;
    transform: translate(-50%, -50%);
  }

  &--pill {
    .free-shipping__copy {
      margin-bottom: 0;
    }
  }

  &--with-pill {
    .free-shipping__track {
      margin-top: 44px;
    }
  }

  &__goal {
    position: absolute;
    top: 50%;
    right: 0;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #c8c8c8;
    transform: translate(50%, -50%);
  }

  &__pill {
    position: absolute;
    bottom: calc(100% + 8px);
    left: clamp(28px, calc(var(--progress) * 100%), calc(100% - 28px));
    padding: 3px 8px;
    border-radius: 8px;
    background: $color-black;
    color: $color-white;
    transform: translateX(-50%);
    white-space: nowrap;
    @include utility-label(10px);
    line-height: 1.35;

    &::after {
      content: "";
      position: absolute;
      left: 50%;
      bottom: -3px;
      width: 6px;
      height: 6px;
      background: $color-black;
      transform: translateX(-50%) rotate(45deg);
    }
  }
}
</style>
